import { alpha, useTheme } from '@mui/material/styles';
import { DragEvent, useEffect, useRef } from 'react';
import { useCommandDashboardTilesStore } from './command-dashboard-tiles-store';
import TileIconBadge from './tile-icon-badge';
import {
  CommandDashboardTileDefinition,
  CommandDashboardTileKey,
  TILE_DRAG_MIME_TYPE,
} from './tile-config';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { MenuItemProps } from '@mui/material/MenuItem';

interface TileLibraryTileProps extends MenuItemProps {
  tile: CommandDashboardTileDefinition;
  onAdd: (key: CommandDashboardTileKey) => void;
  // When set, the tile is disabled and shows this text as a tooltip (eg. explaining a missing
  // permission) instead of just going quietly non-interactive.
  disabledReason?: string;
}

/** An entry in the tile library menu, draggable onto the dashboard. */
export default function TileLibraryTile({
  tile,
  onAdd,
  disabledReason,
  // MenuList clones its children to hand them focus and tab order, so whatever
  // it injects has to reach the MenuItem underneath.
  ...menuItemProps
}: TileLibraryTileProps) {
  const isDisabled = Boolean(disabledReason);
  const theme = useTheme();
  const setDraggingTileKey = useCommandDashboardTilesStore(
    (state) => state.setDraggingTileKey
  );
  const dragPreviewRef = useRef<HTMLElement | null>(null);

  const removeDragPreview = () => {
    dragPreviewRef.current?.remove();
    dragPreviewRef.current = null;
  };

  useEffect(() => removeDragPreview, []);

  /**
   * Chrome's automatic drag image snapshots a region of the menu rather than
   * just the item under the cursor, which drags a slice of the neighbouring
   * options along with it. Handing it a detached clone of the one item removes
   * the ambiguity: the clone has no siblings, so nothing else can be captured.
   *
   * The clone is appended next to the original so it keeps inheriting the same
   * styles, and is fixed off-screen so it never affects the menu's layout.
   */
  const applySingleItemDragImage = (
    e: DragEvent<HTMLElement>,
    source: HTMLElement
  ) => {
    // Absent in jsdom, and the drag still works without a custom image.
    if (typeof e.dataTransfer.setDragImage !== 'function') {
      return;
    }

    const rect = source.getBoundingClientRect();
    const preview = source.cloneNode(true) as HTMLElement;

    // The clone is decoration only: it must not register as a second drag
    // source, and it must not be announced as a duplicate list option.
    preview.removeAttribute('draggable');
    preview.setAttribute('aria-hidden', 'true');

    preview.style.position = 'fixed';
    preview.style.top = '-10000px';
    preview.style.left = '-10000px';
    preview.style.width = `${rect.width}px`;
    preview.style.height = `${rect.height}px`;
    preview.style.margin = '0';
    preview.style.pointerEvents = 'none';

    (source.parentElement ?? document.body).appendChild(preview);
    dragPreviewRef.current = preview;

    e.dataTransfer.setDragImage(
      preview,
      e.clientX - rect.left,
      e.clientY - rect.top
    );
  };

  const handleDragStart = (e: DragEvent<HTMLElement>) => {
    e.dataTransfer.setData(TILE_DRAG_MIME_TYPE, tile.key);
    e.dataTransfer.effectAllowed = 'copy';
    applySingleItemDragImage(e, e.currentTarget);
    setDraggingTileKey(tile.key);
  };

  const menuItem = (
    <MenuItem
      // Not the native `disabled` prop - that stops the element from firing pointer events at
      // all, which breaks Tooltip on hover and would force wrapping it in a <span>. aria-disabled
      // + a guarded onClick achieves the same "can't activate this" outcome while keeping the
      // item focusable and hoverable, and it stays a plain <li> inside MenuList's <ul>.
      aria-disabled={isDisabled || undefined}
      draggable={!isDisabled}
      onDragStart={isDisabled ? undefined : handleDragStart}
      onDragEnd={() => {
        removeDragPreview();
        setDraggingTileKey(null);
      }}
      onClick={isDisabled ? undefined : () => onAdd(tile.key)}
      sx={{
        width: '100%',
        alignItems: 'flex-start',
        gap: 2,
        p: 1,
        border: '1px solid',
        borderColor: alpha(theme.palette.semantic.outline.light, 0.5),
        borderRadius: '4px',
        bgcolor: theme.palette.semantic.surface.bg1,
        opacity: isDisabled ? 0.5 : 1,
        cursor: isDisabled ? 'default' : 'grab',
        '&:active': { cursor: isDisabled ? 'default' : 'grabbing' },
      }}
      {...menuItemProps}
    >
      <TileIconBadge tile={tile} size={40} />
      <Box>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.semantic.text.title2,
            fontWeight: 700,
          }}
        >
          {tile.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: theme.palette.semantic.text.support }}
        >
          {tile.description}
        </Typography>
      </Box>
    </MenuItem>
  );

  if (!disabledReason) {
    return menuItem;
  }

  // No span wrapper needed - the MenuItem above is aria-disabled, not natively disabled, so it
  // still fires the pointer events Tooltip attaches to directly.
  return (
    <Tooltip title={disabledReason} arrow>
      {menuItem}
    </Tooltip>
  );
}
